import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Check, Coffee, Maximize2, Pause, Play, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useExtensionsStore } from '@/stores/extensionsStore';
import { useFocusStore } from './focusStore';
import { FocusDial } from './FocusDial';
import { FocusPanel } from './FocusPanel';
import { FOCUS_ACCENT, FOCUS_TIMER_ID, useClick, useFocusSettings, useNow } from './hooks';
import { RollingClock } from './RollingClock';
import { notify, playChime } from './chime';
import { MINUTE, formatClock, progress, remainingMs } from './time';

/* The pill is one black shape whose width, height and corner ride one
   spring, so it grows as a single object. Its contents do not stretch with
   it: the compact row and the open row crossfade through a small blur and
   scale, the old one leaving a beat before the new one arrives. Pattern
   from Bencho's Dynamic island block. */
// Shorter than the 60px bottom toolbar it sits above, so it never reads as the bigger object.
const COMPACT = { width: 124, height: 40, borderRadius: 20 };
const OPEN = { width: 288, height: 52, borderRadius: 26 };
const SWAP = {
  initial: { opacity: 0, scale: 0.92, filter: 'blur(6px)' },
  animate: { opacity: 1, scale: 1, filter: 'blur(0px)' },
  exit: { opacity: 0, scale: 0.92, filter: 'blur(6px)' },
};

/** Only chime for a ring the user could still hear: not one that ended hours ago in a closed tab. */
const LATE_RING_MS = 2 * MINUTE;

/**
 * Mounted once on the canvas page. Keeps the clock honest (rings, tab title)
 * whether or not the floating pill is showing, and draws the pill.
 */
export function FocusFloater() {
  const enabled = useExtensionsStore((s) => s.isEnabled(FOCUS_TIMER_ID));
  useExtensionsStore((s) => s.rows);
  const loaded = useExtensionsStore((s) => s.loaded);
  const load = useExtensionsStore((s) => s.load);
  const active = useFocusStore((s) => s.active);
  const hidden = useFocusStore((s) => s.floaterHidden);
  const offset = useFocusStore((s) => s.floaterOffset);
  const { extend, pause, resume, finish, start, markRung, setFloaterHidden, setFloaterOffset } = useFocusStore();
  const settings = useFocusSettings();
  const now = useNow(!!active && !active.pausedAt);
  const [panelOpen, setPanelOpen] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const draggedRef = useRef(false);
  const still = useReducedMotion();
  const boundsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!loaded) void load();
  }, [loaded, load]);

  const left = active ? remainingMs(active, now) : 0;
  const ringing = !!active?.rangAt;

  // Ring once when the countdown crosses zero (or on return to a tab where it already has).
  const rungRef = useRef<string | null>(null);
  useEffect(() => {
    if (!enabled || !active || active.rangAt || active.pausedAt || left > 0) return;
    markRung(active.endsAt);
    setExpanded(true);
    // A snoozed timer keeps its id, so key on the end time too.
    const key = `${active.id}:${active.endsAt}`;
    if (rungRef.current === key) return;
    rungRef.current = key;
    if (Date.now() - active.endsAt > LATE_RING_MS) return;
    if (settings.sound) playChime();
    const label = active.kind === 'break' ? 'Break is over' : "Time's up";
    if (document.hidden) notify(label, active.intent || 'Your focus session ended.');
  }, [enabled, active, left, markRung, settings.sound]);

  // Countdown in the tab title, so it is visible from other tabs.
  const baseTitle = useRef<string | null>(null);
  useEffect(() => {
    if (!enabled || !active) {
      if (baseTitle.current !== null) {
        document.title = baseTitle.current;
        baseTitle.current = null;
      }
      return;
    }
    if (baseTitle.current === null) baseTitle.current = document.title;
    const prefix = ringing ? "Time's up" : active.pausedAt ? `${formatClock(left)} paused` : formatClock(left);
    document.title = `${prefix} · ${active.intent || baseTitle.current}`;
  }, [enabled, active, left, ringing]);
  useEffect(
    () => () => {
      if (baseTitle.current !== null) document.title = baseTitle.current;
    },
    [],
  );

  if (!enabled) return null;

  const visible = !!active && !hidden;
  const open = expanded || ringing;
  const spring = still ? { duration: 0 } : { type: 'spring' as const, stiffness: 380, damping: 26, mass: 0.9 };
  const label = ringing ? "Time's up" : active?.intent || (active?.kind === 'break' ? 'Break' : 'Focus');
  // Buttons live inside the pill: a press on one is not a press on the pill.
  const own = (fn: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    fn();
  };

  return (
    <>
      <div ref={boundsRef} className="pointer-events-none fixed inset-2 z-[45]">
        <AnimatePresence>
          {visible && active && (
            <motion.div
              key="focus-floater"
              drag
              dragMomentum={false}
              dragElastic={0}
              dragConstraints={boundsRef}
              initial={{ opacity: 0, scale: 0.9, x: offset.x, y: offset.y + 10 }}
              animate={{ opacity: 1, scale: 1, x: offset.x, y: offset.y }}
              exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.15 } }}
              transition={spring}
              onDragStart={() => {
                draggedRef.current = true;
              }}
              onDragEnd={(_, info) => setFloaterOffset({ x: offset.x + info.offset.x, y: offset.y + info.offset.y })}
              // Stacked above the bottom toolbar (24px + 60px tall, nearly full width), left-aligned.
              className="pointer-events-auto absolute bottom-[88px] left-2 touch-none sm:left-4"
            >
              {/* The swell when it rings, on its own wrapper so it never fights the size spring. */}
              <motion.div
                key={ringing ? 'ringing' : 'quiet'}
                initial={ringing && !still ? { scale: 1.08 } : false}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 500, damping: 18 }}
              >
                <motion.div
                  role="timer"
                  aria-label={`${label}: ${formatClock(left)}${left >= 0 ? ' left' : ' over'}`}
                  aria-expanded={open}
                  tabIndex={0}
                  initial={false}
                  animate={open ? OPEN : COMPACT}
                  transition={spring}
                  whileTap={still ? undefined : { scale: 0.97 }}
                  onPointerDown={() => {
                    draggedRef.current = false;
                  }}
                  onClick={() => {
                    if (draggedRef.current || ringing) return;
                    setExpanded((x) => !x);
                  }}
                  onKeyDown={(e) => {
                    if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget && !ringing) {
                      e.preventDefault();
                      setExpanded((x) => !x);
                    }
                  }}
                  className={cn(
                    'relative cursor-grab select-none overflow-hidden bg-[#0c0c0d] text-white shadow-[0_12px_32px_-8px_rgba(0,0,0,0.45)] outline-none active:cursor-grabbing',
                    'ring-1 ring-white/[0.06] focus-visible:ring-2 focus-visible:ring-white/40',
                  )}
                  style={ringing ? { boxShadow: `0 0 0 2px ${FOCUS_ACCENT}, 0 12px 32px -8px ${FOCUS_ACCENT}88` } : undefined}
                >
                  <AnimatePresence mode="popLayout" initial={false}>
                    {open ? (
                      <motion.div
                        key="open"
                        {...SWAP}
                        transition={{ duration: still ? 0 : 0.22, delay: still ? 0 : 0.06 }}
                        className="absolute inset-0 flex items-center gap-2.5 pl-2 pr-2"
                      >
                        {ringing ? (
                          <IslandButton label="Done" onClick={own(() => finish())} tone="accent">
                            <Check className="h-4 w-4" strokeWidth={2.5} />
                          </IslandButton>
                        ) : (
                          <IslandButton
                            label={active.pausedAt ? 'Resume' : 'Pause'}
                            onClick={own(() => (active.pausedAt ? resume() : pause()))}
                            tone="accent"
                          >
                            {active.pausedAt ? (
                              <Play className="h-3.5 w-3.5 fill-current" strokeWidth={0} />
                            ) : (
                              <Pause className="h-3.5 w-3.5 fill-current" strokeWidth={0} />
                            )}
                          </IslandButton>
                        )}
                        <div className="min-w-0 flex-1 leading-tight">
                          <div className="truncate text-[10px] leading-3 text-white/55">{label}</div>
                          <div
                            className="text-[20px] font-light leading-6 tabular-nums tracking-tight"
                            style={{ color: ringing || active.kind === 'focus' ? FOCUS_ACCENT : undefined }}
                          >
                            <RollingClock ms={left} />
                          </div>
                        </div>
                        <div className="flex items-center gap-0.5">
                          <IslandButton label="Add 5 minutes" onClick={own(() => extend(5 * MINUTE))}>
                            <span className="text-[12px] font-medium tabular-nums">+5</span>
                          </IslandButton>
                          {ringing && active.kind === 'focus' ? (
                            <IslandButton
                              label={`${settings.breakMinutes} minute break`}
                              onClick={own(() => {
                                finish();
                                start({ intent: 'Break', minutes: settings.breakMinutes, kind: 'break' });
                              })}
                            >
                              <Coffee className="h-4 w-4" />
                            </IslandButton>
                          ) : !ringing ? (
                            <>
                              <IslandButton label="Open timer" onClick={own(() => setPanelOpen(true))}>
                                <Maximize2 className="h-3.5 w-3.5" />
                              </IslandButton>
                              <IslandButton label="Hide until it rings" onClick={own(() => setFloaterHidden(true))}>
                                <X className="h-4 w-4" />
                              </IslandButton>
                            </>
                          ) : null}
                        </div>
                      </motion.div>
                    ) : (
                      <motion.div
                        key="compact"
                        {...SWAP}
                        transition={{ duration: still ? 0 : 0.22, delay: still ? 0 : 0.06 }}
                        className="absolute inset-0 flex items-center justify-between pl-2.5 pr-3.5"
                      >
                        <FocusDial
                          size={20}
                          variant="ring"
                          onDark
                          stroke={2.5}
                          value={1 - progress(active, now)}
                          muted={active.kind === 'break'}
                        >
                          {active.pausedAt ? <Pause className="h-2 w-2 fill-white" strokeWidth={0} /> : null}
                        </FocusDial>
                        <span
                          className="text-[15px] font-medium tabular-nums"
                          style={{ color: active.kind === 'focus' ? FOCUS_ACCENT : 'rgba(255,255,255,0.8)' }}
                        >
                          <RollingClock ms={left} />
                        </span>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <FocusPanel open={panelOpen} onOpenChange={setPanelOpen} />
    </>
  );
}

function IslandButton({
  label,
  onClick,
  tone,
  children,
}: {
  label: string;
  onClick: (e: React.MouseEvent) => void;
  tone?: 'accent';
  children: React.ReactNode;
}) {
  const click = useClick();
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={(e) => {
        click();
        onClick(e);
      }}
      onPointerDownCapture={(e) => e.stopPropagation()}
      className={cn(
        'flex shrink-0 items-center justify-center rounded-full transition-[transform,background-color] active:scale-90',
        tone === 'accent' ? 'h-9 w-9' : 'h-8 w-8 text-white/70 hover:bg-white/10 hover:text-white',
      )}
      style={tone === 'accent' ? { backgroundColor: `${FOCUS_ACCENT}33`, color: FOCUS_ACCENT } : undefined}
    >
      {children}
    </button>
  );
}
